import axios, {
  AxiosError,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import { HTTP_STATUS, ErrorCode } from '@stx-shared/constants';
import { logger } from '@/shared/utils/logger';
import { useAuthStore } from '@/store';
import { API_CONFIG } from '@/constants';
import { showToast } from '@/shared/utils/notification';
import { SecureStorage, STORAGE_KEYS } from '@/shared/utils/secure-storage';

/**
 * Axios HTTP Client Configuration
 *
 * Centralized HTTP client with interceptors for:
 * - Authentication (JWT token in HttpOnly cookies)
 * - Error handling with proper logout on token expiry
 * - Request/Response logging (development)
 * - Timeout management
 *
 * Token Flow (NO REFRESH):
 * 1. Login: BE sets accessToken + refreshToken in HttpOnly cookies
 * 2. API calls: Browser automatically sends cookies
 * 3. TOKEN_EXPIRED (401): Logout and redirect to login page
 * 4. Logout: BE clears cookies, FE clears local storage
 */

// Prevent multiple simultaneous logout calls
let isLoggingOut = false;

/**
 * Force logout and redirect to login page
 */
const forceLogout = (message: string): void => {
  if (!isLoggingOut) {
    isLoggingOut = true;
    logger.warn('Forcing logout', { message });
    showToast({
      type: 'error',
      title: 'Session Expired',
      message,
    });
    useAuthStore.getState().logout();
    setTimeout(() => {
      isLoggingOut = false;
    }, 1000);
  }
};

/**
 * Common error messages
 */
const SESSION_EXPIRED_MESSAGE = 'Session expired. Please log in again.';

/**
 * Error code to logout message mapping
 */
const AUTH_ERROR_MESSAGES: Record<string, string> = {
  [ErrorCode.SESSION_EXPIRED]:
    'Your session has been closed. Please log in again.',
  [ErrorCode.LOGGED_IN_ELSEWHERE]:
    'You have been logged out because your account was accessed from another device.',
  [ErrorCode.TOKEN_EXPIRED]: SESSION_EXPIRED_MESSAGE,
  [ErrorCode.REFRESH_TOKEN_EXPIRED]: SESSION_EXPIRED_MESSAGE,
  [ErrorCode.INVALID_TOKEN]: 'Invalid session. Please log in again.',
  [ErrorCode.TENANT_NOT_CONNECTED]:
    'Session disconnected. Please log in again.',
  [ErrorCode.UNAUTHORIZED]: SESSION_EXPIRED_MESSAGE,
  [ErrorCode.MISSING_REFRESH_TOKEN]: SESSION_EXPIRED_MESSAGE,
};

/**
 * Handle specific auth error codes from backend
 */
const handleAuthErrorCode = (errorCode: string | undefined): void => {
  // Check if we have a mapped error message
  if (errorCode && AUTH_ERROR_MESSAGES[errorCode]) {
    // For UNAUTHORIZED and MISSING_REFRESH_TOKEN, only logout if user data exists
    if (
      errorCode === ErrorCode.UNAUTHORIZED ||
      errorCode === ErrorCode.MISSING_REFRESH_TOKEN
    ) {
      if (SecureStorage.getItem(STORAGE_KEYS.USER_DATA)) {
        forceLogout(AUTH_ERROR_MESSAGES[errorCode]);
      }
    } else {
      // All other error codes: always logout
      forceLogout(AUTH_ERROR_MESSAGES[errorCode]);
    }
  } else {
    // Unknown error code: logout if user session exists
    if (SecureStorage.getItem(STORAGE_KEYS.USER_DATA)) {
      logger.warn('Unknown 401 error with user session - logging out');
      forceLogout(SESSION_EXPIRED_MESSAGE);
    }
  }
};

/**
 * Handle 401 Unauthorized errors
 */
const handle401Error = (error: AxiosError): void => {
  const isLoginPage = window.location.pathname === '/login';

  // Get error code from backend response
  const errorData = error.response?.data as {
    error?: { code?: string };
    code?: string;
  };
  const errorCode = errorData?.error?.code || errorData?.code;

  // Log detailed 401 info for debugging
  logger.warn('401 Unauthorized received', {
    url: error.config?.url,
    method: error.config?.method,
    errorCode,
    isLoginPage,
    hasUserData: !!SecureStorage.getItem(STORAGE_KEYS.USER_DATA),
    timestamp: new Date().toISOString(),
  });

  // Skip handling for login page
  if (isLoginPage) {
    return;
  }

  // Handle based on error code from BE
  handleAuthErrorCode(errorCode);
};

/**
 * Handle other HTTP error statuses
 */
const handleOtherErrors = (error: AxiosError): void => {
  if (error.response) {
    const status = error.response.status;

    switch (status) {
      case HTTP_STATUS.FORBIDDEN:
        logger.warn('Insufficient permissions');
        break;
      case HTTP_STATUS.NOT_FOUND:
        logger.warn('Resource not found');
        break;
      case HTTP_STATUS.TOO_MANY_REQUESTS:
        logger.warn('Too many requests - please slow down');
        break;
      case HTTP_STATUS.INTERNAL_SERVER_ERROR:
      case HTTP_STATUS.BAD_GATEWAY:
      case HTTP_STATUS.SERVICE_UNAVAILABLE:
      case HTTP_STATUS.GATEWAY_TIMEOUT:
        logger.error('Server error - please try again later');
        break;
      default:
        logger.warn('An error occurred');
    }
  } else if (error.request) {
    logger.error('Network error - please check your connection');
  } else {
    logger.error('Request failed', error);
  }
};

/**
 * Main Axios instance for API calls
 */
export const apiClient = axios.create({
  baseURL: API_CONFIG.BASE_URL, // Only base URL, no prefix/version
  timeout: API_CONFIG.TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Send cookies with requests
});

/**
 * Request Interceptor
 * Logs requests in development mode
 * Note: Authentication tokens are sent automatically via HttpOnly cookies
 */
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Log request in development
    if (import.meta.env.DEV) {
      logger.debug('API Request', {
        method: config.method?.toUpperCase(),
        url: config.url,
        data: config.data,
      });
    }

    return config;
  },
  error => {
    logger.error('Request Error', error);
    return Promise.reject(error);
  },
);

/**
 * Response Interceptor
 * Handles responses, errors, and token refresh globally
 */
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    // Log response in development
    if (import.meta.env.DEV) {
      logger.debug('API Response', {
        method: response.config.method?.toUpperCase(),
        url: response.config.url,
        status: response.status,
        data: response.data,
      });
    }

    return response;
  },
  async (error: AxiosError) => {
    // Log error in development
    if (import.meta.env.DEV) {
      logger.apiError('API Error', error, {
        method: error.config?.method?.toUpperCase(),
        url: error.config?.url,
        status: error.response?.status,
      });
    }

    // Handle 401 Unauthorized - Token expired or invalid
    if (error.response?.status === HTTP_STATUS.UNAUTHORIZED) {
      handle401Error(error);
      return Promise.reject(error);
    }

    // Handle other error statuses
    handleOtherErrors(error);
    return Promise.reject(error);
  },
);

const DEFAULT_ERROR_MESSAGE = 'An error occurred';

/**
 * Helper function to get error message from API response
 * Handles validation errors with constraints from backend
 */
export const getErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as Record<string, unknown>;

    // Check for validation errors with constraints
    if (data?.errorCode === 'VALIDATION_ERROR' && data?.details) {
      const details = data.details as {
        constraints?: string[];
        field?: string;
      };
      if (details.constraints && details.constraints.length > 0) {
        // Return first constraint without field prefix for cleaner message
        // e.g., "name: Name must contain only letters" -> "Name must contain only letters"
        const firstConstraint = details.constraints[0];
        const colonIndex = firstConstraint.indexOf(': ');
        return colonIndex > -1
          ? firstConstraint.substring(colonIndex + 2)
          : firstConstraint;
      }
      // Include field context when available for validation errors
      const mainMsg = (data?.message as string) || error.message;
      if (details.field) {
        return `[${details.field}] ${mainMsg}`;
      }
      return mainMsg || DEFAULT_ERROR_MESSAGE;
    }

    // For database errors, include detail from the operation/table context
    if (data?.errorCode === 'DATABASE_ERROR' && data?.details) {
      const details = data.details as {
        operation?: string;
        table?: string;
        error?: string;
      };
      const mainMsg = (data?.message as string) || error.message;
      // The message already includes cause (e.g., "Failed to save FX Request: <cause>")
      // Append table context if available for operators
      if (details.table) {
        return `${mainMsg} (table: ${details.table})`;
      }
      return mainMsg || DEFAULT_ERROR_MESSAGE;
    }

    return (data?.message as string) || error.message || DEFAULT_ERROR_MESSAGE;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'An unknown error occurred';
};

/**
 * Extract error message from blob-type error responses.
 * When responseType is 'blob' and the server returns a JSON error,
 * axios delivers error.response.data as a Blob instead of parsed JSON.
 */
export const getBlobErrorMessage = async (error: unknown): Promise<string> => {
  if (axios.isAxiosError(error) && error.response?.data instanceof Blob) {
    try {
      const text = await error.response.data.text();
      const parsed = JSON.parse(text) as Record<string, unknown>;
      return (parsed.message as string) || DEFAULT_ERROR_MESSAGE;
    } catch {
      return error.message || DEFAULT_ERROR_MESSAGE;
    }
  }
  return getErrorMessage(error);
};

/**
 * Check if an axios error is a "not found" (404) response.
 * Works with both JSON and blob responseType requests.
 */
export const isNotFoundError = (error: unknown): boolean => {
  return (
    axios.isAxiosError(error) &&
    error.response?.status === HTTP_STATUS.NOT_FOUND
  );
};

export default apiClient;
